import { useId, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  type LocationOption,
  type PrimarySoftwareOption,
  type SupplementalSoftwareOption,
} from '@/features/admin/staffing/workspace-posting-field-options'
import { ActionToggle } from '@/components/ActionToggle'
import { Avatar } from '@/components/Avatar'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { Form, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { capitalizeFirstLetter } from '@/utils/string-util'
import { formatOpportunityTypeOption } from '@/features/admin/staffing/workspace-posting-formatters'
import { type WorkspacePostingOpportunityType } from '@/features/admin/staffing/WorkspacePosting'
import {
  WorkspacePostingDialog,
  WorkspacePostingNumericInputs,
  WorkspacePostingOutsourcingRow,
  WorkspacePostingTypeOfWorkField,
} from '@/features/admin/staffing/components/WorkspacePostingDialog'
import { SelectItem, SingleItemSelector } from '@/components/SingleItemSelector'
import { type WorkspacePostingDialogViewModel } from './WorkspacePostingDialogViewModel'

export interface WorkspacePostingDialogContainerProps {
  vm: WorkspacePostingDialogViewModel
  title: string
  buttonText: string
}

export const WorkspacePostingDialogContainer = observer(
  function WorkspacePostingDialogContainer({
    vm,
    title,
    buttonText,
  }: WorkspacePostingDialogContainerProps) {
    const formId = useId()

    return (
      <WorkspacePostingDialog
        showing={vm.modal.showing}
        onClose={vm.close}
        title={title}
        buttonText={buttonText}
        isSubmitDisabled={!vm.canSubmit}
        isSubmitPending={vm.submit.pending}
        formId={formId}
      >
        <Form id={formId} onSubmit={vm.submit}>
          <FormRow>
            <FormGroupContainer label="Select workspace">
              <SingleItemSelector
                value={vm.workspace}
                itemOptions={vm.workspaces}
                itemToString={(option?: Workspace) =>
                  option ? option.name : 'Select a workspace'
                }
                itemPrefix={(item: Workspace) => (
                  <Avatar
                    size={24}
                    subject={{
                      displayName: item.name,
                      avatar: item.branding.logos.icon,
                    }}
                  />
                )}
                onChange={vm.setWorkspace}
                isDisabled={
                  Boolean(vm.workspacePosting?.id) || vm.submit.pending
                }
                testId="custom-select"
              />
            </FormGroupContainer>
          </FormRow>

          <FormRow>
            <FormGroupContainer label="Select opportunity type">
              <SingleItemSelector
                value={vm.opportunityType}
                itemOptions={['SignAndFile', 'Outsourcing']}
                itemToString={(option?: WorkspacePostingOpportunityType) =>
                  formatOpportunityTypeOption(option)
                }
                onChange={vm.setOpportunityType}
                isDisabled={
                  Boolean(vm.workspacePosting?.id) || vm.submit.pending
                }
                testId="custom-select"
              />
            </FormGroupContainer>
          </FormRow>

          <FormRow>
            <FormGroupContainer label="Location">
              <SingleItemSelector
                value={vm.location}
                itemOptions={vm.locationItems}
                itemToString={(item: LocationOption) => item}
                onChange={vm.setLocation}
                isDisabled={vm.submit.pending}
                testId="custom-select"
              />
            </FormGroupContainer>
          </FormRow>

          <WorkspacePostingOutsourcingRow isOutsourcing={vm.isOutsourcing}>
            <FormGroupContainer label="Primary software">
              <SingleItemSelector
                value={vm.primarySoftware}
                itemOptions={vm.primarySoftwareItems}
                itemToString={(item: PrimarySoftwareOption) => item}
                onChange={vm.setPrimarySoftware}
                isDisabled={vm.submit.pending}
                testId="custom-select"
              />
            </FormGroupContainer>
          </WorkspacePostingOutsourcingRow>

          <WorkspacePostingOutsourcingRow isOutsourcing={vm.isOutsourcing}>
            <ActionToggle
              label="Backup required"
              description="Enable if the primary software requires a manual backup."
              value={vm.backupRequired}
              onChange={vm.toggleBackupRequired}
              isThemed
            />
          </WorkspacePostingOutsourcingRow>

          <WorkspacePostingOutsourcingRow isOutsourcing={vm.isOutsourcing}>
            <FormGroupContainer label="Supplemental software">
              <SingleItemSelector
                value={vm.supplementalSoftware}
                itemOptions={vm.supplementalSoftwareItems}
                itemToString={(item: SupplementalSoftwareOption) => item}
                onChange={vm.setSupplementalSoftware}
                testId="custom-select"
              />
            </FormGroupContainer>
          </WorkspacePostingOutsourcingRow>

          <WorkspacePostingOutsourcingRow isOutsourcing={vm.isOutsourcing}>
            <FormGroupContainer label="Specialties">
              <SpecialtiesSelectorContainer
                vm={vm}
                isDisabled={vm.submit.pending}
                testId="custom-select"
              />
            </FormGroupContainer>
          </WorkspacePostingOutsourcingRow>

          <WorkspacePostingNumericInputs>
            <NumericInputContainer
              label="Pros required"
              field="prosRequired"
              model={vm}
              error={vm.validation.getError('prosRequired')}
            />
            <NumericInputContainer
              label="Max concurrent workspaces"
              field="maxConcurrentWorkspaces"
              model={vm}
              error={vm.validation.getError('maxConcurrentWorkspaces')}
            />
          </WorkspacePostingNumericInputs>

          <WorkspacePostingTypeOfWorkField
            items={vm.jobTypeItems.map((type) => ({
              type,
              label: capitalizeFirstLetter(type),
              active: vm.typeOfWork.includes(type),
              onToggle: () => vm.toggleTypeOfWork(type),
            }))}
          />

          <FormRow>
            <TextAreaContainer
              label="Description"
              field="description"
              model={vm}
              rows={4}
              minLength={1}
              maxLength={1024}
              error={vm.validation.getError('description')}
              help="Max 1024 characters."
            />
          </FormRow>
        </Form>
      </WorkspacePostingDialog>
    )
  },
)

interface SpecialtiesSelectorContainerProps {
  vm: WorkspacePostingDialogViewModel
  isDisabled?: boolean
  testId?: string
}

const SpecialtiesSelectorContainer = observer(
  function SpecialtiesSelectorContainer({
    vm,
    isDisabled,
    testId,
  }: SpecialtiesSelectorContainerProps) {
    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.specialtyItems, (item, text) =>
          item.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.specialtyItems],
    )
    const selectProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <CustomSelect
        {...selectProps}
        value={vm.specialties}
        onChange={vm.setSpecialties}
        multi={true}
        isDisabled={isDisabled}
        testId={testId}
        renderSearchItem={({ item, ...itemProps }) => (
          <SelectItem {...itemProps}>{item}</SelectItem>
        )}
      />
    )
  },
)
